/**
 * Upload HMS's lube list, exactly as HMS sends it.
 *
 * The list is the COMPLETE current list (Chris, 2026-09-21). Every row lands in
 * a pile -- unchanged, corrected, new name for an existing product, new, back on
 * the list, or needs a look -- and every current product that is not on the
 * list is retired: hidden from the product picker, kept on every past deal.
 *
 * The rules live on the server (stock/services/supplier_list.py). This screen
 * only shows the piles and sends back the uploader's answers; it never decides
 * anything itself, so the screen and the rules cannot disagree.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faClockRotateLeft, faFileExcel, faTimes, faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';

import { stockService } from '../../services/stockService';
import './ProductCsvImportModal.css';
import './SupplierListUploadModal.css';

const PILES = [
    { key: 'needs_look', label: 'Needs a look' },
    { key: 'new_version', label: 'New name for an existing product' },
    { key: 'new', label: 'New products' },
    { key: 'corrected', label: 'Corrected' },
    { key: 'back', label: 'Back on the list' },
    { key: 'retiring', label: 'Retiring' },
    { key: 'unchanged', label: 'Unchanged' },
    { key: 'duplicate', label: 'Listed twice' },
];

const FIELD_WORDS = { code: 'Code', description: 'Description', brand: 'Brand' };

const errorText = (e, fallback) => {
    const body = e?.response?.data || {};
    return body.error || body.detail || fallback;
};

const formatWhen = (iso) => {
    try {
        return new Date(iso).toLocaleString(undefined, {
            day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
        });
    } catch (e) {
        return iso;
    }
};

const SupplierListUploadModal = ({ isOpen, onClose, onApplied }) => {
    const [file, setFile] = useState(null);
    const [restoreFrom, setRestoreFrom] = useState(null);   // a past list being restored
    const [answers, setAnswers] = useState({});
    const [plan, setPlan] = useState(null);
    const [pile, setPile] = useState('needs_look');
    const [confirmLarge, setConfirmLarge] = useState(false);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const [history, setHistory] = useState([]);
    const [newBrand, setNewBrand] = useState({});

    const reset = useCallback(() => {
        setFile(null);
        setRestoreFrom(null);
        setAnswers({});
        setPlan(null);
        setConfirmLarge(false);
        setMessage('');
        setNewBrand({});
    }, []);

    const loadHistory = useCallback(async () => {
        try {
            setHistory(await stockService.supplierListHistory());
        } catch (e) {
            setHistory([]);
        }
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        reset();
        loadHistory();
    }, [isOpen, reset, loadHistory]);

    // Re-check the whole list on every answer: one answer can raise the next
    // question ("not the same product" -> "then which brand?").
    useEffect(() => {
        if (!isOpen || (!file && !restoreFrom)) return undefined;
        let cancelled = false;
        (async () => {
            setBusy(true);
            setMessage('');
            try {
                const data = restoreFrom
                    ? await stockService.restoreSupplierList(restoreFrom.id, { preview: true, answers })
                    : await stockService.reviewSupplierList(file, answers);
                if (cancelled) return;
                setPlan(data);
                setPile((current) => {
                    const count = (key) => (key === 'retiring' ? data.summary.retired : data.summary[key]) || 0;
                    if (count('needs_look') > 0) return 'needs_look';
                    if (count(current) > 0) return current;
                    return (PILES.find((p) => count(p.key) > 0) || PILES[0]).key;
                });
            } catch (e) {
                if (!cancelled) {
                    setPlan(null);
                    setMessage(errorText(e, 'The list could not be read.'));
                }
            } finally {
                if (!cancelled) setBusy(false);
            }
        })();
        return () => { cancelled = true; };
    }, [isOpen, file, restoreFrom, answers]);

    const answer = (row, value) => {
        setAnswers((prev) => ({ ...prev, [String(row)]: { ...(prev[String(row)] || {}), ...value } }));
    };

    const clearAnswer = (row) => {
        setAnswers((prev) => {
            const next = { ...prev };
            delete next[String(row)];
            return next;
        });
    };

    const answerBrandForAll = (brand) => {
        if (!brand || !plan) return;
        setAnswers((prev) => {
            const next = { ...prev };
            plan.rows
                .filter((e) => e.pile === 'needs_look' && e.question === 'brand')
                .forEach((e) => { next[String(e.row)] = { ...(next[String(e.row)] || {}), brand }; });
            return next;
        });
    };

    const summary = plan?.summary;
    const counts = useMemo(() => {
        if (!summary) return {};
        return { ...summary, retiring: summary.retired };
    }, [summary]);

    const visibleRows = useMemo(() => {
        if (!plan || pile === 'retiring') return [];
        return plan.rows.filter((e) => e.pile === pile);
    }, [plan, pile]);

    const blocked = (summary?.needs_look || 0) > 0;
    const needsLargeConfirm = summary?.large_retire && !confirmLarge;

    const apply = async () => {
        setBusy(true);
        setMessage('');
        try {
            const result = restoreFrom
                ? await stockService.restoreSupplierList(restoreFrom.id, { answers, confirmLargeRetire: confirmLarge })
                : await stockService.applySupplierList(file, answers, { confirmLargeRetire: confirmLarge });
            const s = result.summary || {};
            onApplied?.({
                ...result,
                message: `Lube list applied: ${s.new_version || 0} new names, ${s.new || 0} new products, `
                    + `${s.corrected || 0} corrected, ${s.retired || 0} retired.`,
            });
        } catch (e) {
            const body = e?.response?.data || {};
            if (body.rows) setPlan(body);
            setMessage(errorText(e, 'The list could not be applied.'));
        } finally {
            setBusy(false);
        }
    };

    if (!isOpen) return null;

    const renderQuestion = (e) => {
        const mine = answers[String(e.row)] || {};
        if (e.question === 'brand') {
            // Picking or typing only fills a draft; "Use" commits it. Committing
            // straight from the dropdown would move the row out of this pile
            // before "use for all rows asking" could be pressed.
            const brands = plan?.brands || [];
            const draft = (newBrand[e.row] || '').trim().toUpperCase();
            return (
                <div className="slu-question">
                    <select
                        className="pci-select slu-inline"
                        value={brands.includes(draft) ? draft : ''}
                        onChange={(ev) => setNewBrand((p) => ({ ...p, [e.row]: ev.target.value }))}
                        disabled={busy}
                        aria-label={`Brand for row ${e.row}`}
                    >
                        <option value="">Pick a brand…</option>
                        {brands.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                    <input
                        className="pci-select slu-inline"
                        placeholder="…or type a new brand"
                        value={brands.includes(draft) ? '' : (newBrand[e.row] || '')}
                        onChange={(ev) => setNewBrand((p) => ({ ...p, [e.row]: ev.target.value }))}
                        aria-label={`New brand for row ${e.row}`}
                    />
                    <button
                        type="button"
                        className="pci-btn pci-btn-secondary slu-small"
                        disabled={!draft || busy}
                        onClick={() => answer(e.row, { brand: draft })}
                    >
                        Use
                    </button>
                    <button
                        type="button"
                        className="pci-btn pci-btn-secondary slu-small"
                        disabled={!draft || busy}
                        onClick={() => answerBrandForAll(draft)}
                        title="Give every row still asking for a brand this same brand"
                    >
                        Use for all rows asking
                    </button>
                </div>
            );
        }
        if (e.question === 'replaces') {
            return (
                <div className="slu-question">
                    <select
                        className="pci-select slu-inline"
                        value={mine.replaces || ''}
                        onChange={(ev) => answer(e.row, { replaces: ev.target.value })}
                        disabled={busy}
                        aria-label={`Which product row ${e.row} is`}
                    >
                        <option value="">Which product is this?</option>
                        {e.candidates.map((c) => (
                            <option key={c.id} value={c.id}>
                                New name for {c.name} ({c.code}{c.brand ? `, ${c.brand}` : ''})
                            </option>
                        ))}
                        <option value="new">None of these, it is a new product</option>
                    </select>
                </div>
            );
        }
        return <div className="slu-fix-in-sheet">Fix this in the sheet and upload it again.</div>;
    };

    const renderWhat = (e) => {
        if (e.pile === 'needs_look') {
            return (
                <>
                    <div>{e.reason}</div>
                    {renderQuestion(e)}
                </>
            );
        }
        if (e.pile === 'new_version') {
            return (
                <label className="slu-same">
                    <input
                        type="checkbox"
                        checked={(answers[String(e.row)] || {}).replaces !== 'new'}
                        onChange={(ev) => answer(e.row, { replaces: ev.target.checked ? e.product.id : 'new' })}
                        disabled={busy}
                    />
                    Replaces <strong>{e.product.name}</strong> ({e.product.code}). Past deals keep the old name.
                </label>
            );
        }
        if (e.pile === 'corrected' || e.pile === 'back') {
            const bits = Object.entries(e.changes || {}).map(
                ([field, c]) => `${FIELD_WORDS[field] || field}: ${c.from || '(empty)'} → ${c.to || '(empty)'}`,
            );
            return [e.reason, ...bits].filter(Boolean).join(' · ') || 'On the list again.';
        }
        if (e.pile === 'new') {
            const mine = answers[String(e.row)] || {};
            return (
                <>
                    New product under {e.brand}.
                    {mine.replaces === 'new' && (
                        <button
                            type="button"
                            className="pci-btn pci-btn-secondary slu-small slu-undo"
                            onClick={() => clearAnswer(e.row)}
                            disabled={busy}
                        >
                            Undo: it is a new name for an existing product
                        </button>
                    )}
                </>
            );
        }
        return e.reason || '';
    };

    return (
        <div className="pci-overlay" role="dialog" aria-modal="true" aria-label="Upload IMT lube list">
            <div className="pci-modal slu-modal">
                <div className="pci-header">
                    <h2>
                        <FontAwesomeIcon icon={restoreFrom ? faClockRotateLeft : faFileExcel} />
                        {restoreFrom ? `Restore the list from ${formatWhen(restoreFrom.uploaded_at)}` : 'Upload IMT lube list'}
                    </h2>
                    <button type="button" className="pci-close" onClick={onClose} aria-label="Close">
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="pci-body">
                    {!restoreFrom && (
                        <>
                            <p className="pci-hint">
                                Upload IMT&apos;s lube list exactly as they send it (Excel or CSV). It is
                                treated as the <strong>complete</strong> list: anything not on it is retired.
                                Retired lubes disappear from the product pick lists but stay on every past deal.
                                Nothing changes until you press Apply.
                            </p>
                            <input
                                type="file"
                                accept=".xlsx,.csv"
                                className="pci-file"
                                aria-label="IMT lube list file"
                                onChange={(ev) => { setAnswers({}); setConfirmLarge(false); setFile(ev.target.files?.[0] || null); }}
                                disabled={busy}
                            />
                        </>
                    )}

                    {message && (
                        <div className="pci-errors">
                            <div className="pci-errors-head">
                                <FontAwesomeIcon icon={faTriangleExclamation} /> {message}
                            </div>
                        </div>
                    )}

                    {busy && !plan && <p className="pci-hint">Reading the list…</p>}

                    {summary && (
                        <>
                            <div className="slu-piles" role="tablist">
                                {PILES.map((p) => (
                                    <button
                                        key={p.key}
                                        type="button"
                                        role="tab"
                                        aria-selected={pile === p.key}
                                        className={`slu-pile slu-pile-${p.key} ${pile === p.key ? 'active' : ''}`}
                                        onClick={() => setPile(p.key)}
                                        disabled={!counts[p.key]}
                                    >
                                        <strong>{counts[p.key] || 0}</strong> {p.label}
                                    </button>
                                ))}
                            </div>

                            {summary.warnings > 0 && (
                                <p className="pci-hint">
                                    ⚠️ {summary.warnings} row(s) carry a warning (for example a code or description
                                    longer than IMT&apos;s own limit). They are applied as written, not refused.
                                </p>
                            )}

                            {summary.kept_for_now > 0 && (
                                <p className="pci-hint">
                                    {summary.kept_for_now} current product(s) are kept as they are until the
                                    &quot;needs a look&quot; rows are answered.
                                </p>
                            )}

                            {pile === 'retiring' ? (
                                <div className="pci-table-wrap">
                                    <table className="pci-table">
                                        <thead>
                                            <tr><th>Product</th><th>Code</th><th>Brand</th><th>Why</th></tr>
                                        </thead>
                                        <tbody>
                                            {(plan.retiring || []).map((r) => (
                                                <tr key={r.id}>
                                                    <td>{r.name}</td>
                                                    <td>{r.code}</td>
                                                    <td>{r.brand}</td>
                                                    <td>
                                                        {r.replaced_by_row
                                                            ? `Replaced by the new name on row ${r.replaced_by_row}`
                                                            : 'Not on this list'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="pci-table-wrap">
                                    <table className="pci-table">
                                        <thead>
                                            <tr><th>Row</th><th>Product</th><th>Code</th><th>What happens</th></tr>
                                        </thead>
                                        <tbody>
                                            {visibleRows.map((e) => (
                                                <tr key={e.row} className={`slu-row-${e.pile}`}>
                                                    <td>{e.row}</td>
                                                    <td>
                                                        {e.name || <em>(no name)</em>}
                                                        {e.warnings?.length > 0 && (
                                                            <span className="slu-warn" title={e.warnings.join('\n')}> ⚠️</span>
                                                        )}
                                                    </td>
                                                    <td>{e.code}</td>
                                                    <td>{renderWhat(e)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {summary.large_retire && (
                                <label className="slu-confirm">
                                    <input
                                        type="checkbox"
                                        checked={confirmLarge}
                                        onChange={(ev) => setConfirmLarge(ev.target.checked)}
                                    />
                                    This retires <strong>{summary.retired}</strong> of the{' '}
                                    {summary.current_products} current lubes. Yes, this is the right file.
                                </label>
                            )}
                        </>
                    )}

                    {!file && !restoreFrom && history.length > 0 && (
                        <div className="slu-history">
                            <div className="pci-label">Past lists</div>
                            <table className="pci-table">
                                <tbody>
                                    {history.map((h) => (
                                        <tr key={h.id}>
                                            <td>{formatWhen(h.uploaded_at)}</td>
                                            <td>{h.uploaded_by}</td>
                                            <td>{h.file_name}</td>
                                            <td>
                                                {h.summary?.new_version || 0} new names · {h.summary?.new || 0} new ·{' '}
                                                {h.summary?.retired || 0} retired
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="pci-btn pci-btn-secondary slu-small"
                                                    onClick={() => { setAnswers({}); setRestoreFrom(h); }}
                                                    title="Check what restoring this list would change. Nothing changes until you press Apply."
                                                >
                                                    Restore this list
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className="pci-footer">
                    <button type="button" className="pci-btn pci-btn-secondary" onClick={onClose} disabled={busy}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="pci-btn pci-btn-primary"
                        onClick={apply}
                        disabled={!summary || blocked || needsLargeConfirm || busy}
                        title={blocked ? 'Answer the rows that need a look first' : undefined}
                    >
                        {blocked ? `Answer ${summary.needs_look} row(s) first` : 'Apply list'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SupplierListUploadModal;
